import {
	type Actor,
	type BusinessId,
	can,
	type Permission,
} from "@nwga/auth/permissions";
import type { LucideIcon } from "lucide-react";
import {
	BriefcaseIcon,
	BuildingIcon,
	HomeIcon,
	InfoIcon,
	LayoutDashboardIcon,
	ScrollTextIcon,
	ShieldCheckIcon,
	UserIcon,
	UsersIcon,
} from "lucide-react";

/**
 * Where things appear in the interface.
 *
 * This is the fourth composition point, alongside the router, the event catalog
 * and the task registry: a feature that has a place in the UI declares it here,
 * once, and the header, the mobile menu, the footer and the business workspace
 * all pick it up. Nothing below reaches into a component to add a link.
 *
 * It exists because job listings are the *first* feature of this site, not the
 * only one it will ever have. A nav hard-coded into `app-header.tsx` and
 * duplicated into `app-footer.tsx` and again into a dashboard layout is three
 * places to forget; a list is one, and the type makes the questions that matter
 * — who may see this, what does it require — impossible to skip.
 *
 * Paths are written as literals so TanStack Router still type-checks every
 * `to`: a section pointing at a route that does not exist fails `bun run
 * typecheck` rather than rendering a dead link.
 */

export interface Section {
	/** The route path. Type-checked against the generated route tree at the call site. */
	readonly to: string;
	readonly label: string;
	readonly icon: LucideIcon;
	/** One line. The mobile menu shows it under the label; empty states reuse it. */
	readonly description: string;
	/**
	 * Whether the link is active only on an exact match.
	 *
	 * Sections with children — `/jobs` and a listing underneath it — want prefix
	 * matching, so the section stays lit while you are inside it. An index route
	 * that shares its prefix with its siblings wants `exact`.
	 *
	 * Required rather than optional so every entry answers it. `as const` on the
	 * lists below narrows each object to exactly the keys it wrote, and a key
	 * only some of them carry is unreadable across the union.
	 */
	readonly exact: boolean;
}

/**
 * Who a top-level section is shown to.
 *
 * Not a security control — every route guards itself, and the server refuses
 * regardless. This is what stops the header advertising a page that would only
 * bounce an anonymous visitor to sign-in.
 */
export type Audience = "everyone" | "signed-in";

export interface TopLevelSection extends Section {
	readonly audience: Audience;
	/**
	 * A platform-wide permission the viewer must hold, asked without a business
	 * scope: "may this person moderate anything at all?"
	 *
	 * Same status as `audience` — an affordance, not a gate. `/admin` guards
	 * itself in `beforeLoad`, and every procedure it calls authorises again.
	 */
	readonly permission?: Permission;
}

/**
 * The public sections of the platform.
 *
 * Home first, because this site now has one: it is still reached from the Jobs
 * tab on northwestgeorgia.us, but a visitor who lands deep — on a shared
 * listing, say — needs somewhere on *this* site that explains what it is.
 *
 * Job listings are the first service here, not the only one it will have. The
 * shape is what matters: adding "Business directory" or "Local events" later is
 * a line here, not an edit to four components.
 */
export const PUBLIC_SECTIONS = [
	{
		to: "/",
		label: "Home",
		icon: HomeIcon,
		description: "What NWGA Connect offers the region.",
		audience: "everyone",
		// An index route sharing its prefix with every sibling: without this, Home
		// would stay lit on every page of the site.
		exact: true,
	},
	{
		to: "/jobs",
		label: "Jobs",
		icon: BriefcaseIcon,
		description: "Openings from employers across the region.",
		audience: "everyone",
		exact: false,
	},
	{
		to: "/about",
		label: "About",
		icon: InfoIcon,
		description: "Who runs this platform, and what it costs.",
		audience: "everyone",
		exact: false,
	},
] as const satisfies readonly TopLevelSection[];

/**
 * Sections that only exist once you are signed in, shown beside the public ones.
 *
 * Kept separate rather than filtered out of one list because the header renders
 * them differently — these are *your* things, not the site's.
 *
 * The admin console is here rather than only in the account menu because it is
 * where a moderator spends their whole session. Buried under an avatar, the
 * console was reachable but not *present*, and the first thing an admin saw
 * after signing in was a list of businesses they do not own.
 */
export const SIGNED_IN_SECTIONS = [
	{
		to: "/dashboard",
		label: "Dashboard",
		icon: LayoutDashboardIcon,
		description: "Manage your business and its listings.",
		audience: "signed-in",
		exact: false,
	},
	{
		to: "/admin",
		label: "Admin",
		icon: ShieldCheckIcon,
		description: "Review businesses, listings and accounts.",
		audience: "signed-in",
		permission: "admin.access",
		exact: false,
	},
] as const satisfies readonly TopLevelSection[];

/**
 * One entry of the top-level navigation, with its path narrowed to a literal.
 *
 * The literal is what keeps `<Link to={section.to}>` type-checked: widening it
 * to `string` would compile and then render a dead link.
 */
export type TopLevelSectionEntry =
	| (typeof PUBLIC_SECTIONS)[number]
	| (typeof SIGNED_IN_SECTIONS)[number];

/**
 * The header and mobile menu, in order.
 *
 * One function rather than the two lists spliced together at each call site,
 * because "which of these does this person see" is a question the registry
 * should answer once. It takes an `Actor` — the same object the server
 * authorises with — so the answer comes from `can()` rather than from a role
 * string compared by hand in a component.
 */
export function topLevelSectionsFor(
	actor: Actor | null,
): readonly TopLevelSectionEntry[] {
	return [...PUBLIC_SECTIONS, ...SIGNED_IN_SECTIONS].filter((section) => {
		if (section.audience === "signed-in" && actor === null) return false;

		// `"permission" in section` rather than `section.permission !== undefined`:
		// `as const` gives each entry exactly the keys it wrote, and the ones with
		// no requirement have no such key at all.
		if (!("permission" in section)) return true;

		return actor !== null && can(actor, section.permission);
	});
}

/**
 * The front door.
 *
 * This site is still the sub-site behind the Jobs tab on northwestgeorgia.us —
 * the header leads with the way back, and nothing here introduces the Authority
 * a second time. But it is more than a board now, so `/` is a page rather than
 * a redirect: it says what the platform is, and it is where the logo, the first
 * breadcrumb and signing out all land.
 */
export const HOME_PATH = "/" as const;

/**
 * A section of one business's workspace.
 *
 * `permission` is business-scoped and checked against the viewer's role in
 * *that* business, so a member who cannot manage people does not see a Team tab
 * that 403s when they open it. The server checks again regardless; this is an
 * affordance, not a gate.
 */
export interface WorkspaceSection extends Section {
	readonly permission?: Permission;
}

export const WORKSPACE_SECTIONS = [
	{
		to: "/dashboard/$businessSlug",
		label: "Overview",
		icon: LayoutDashboardIcon,
		description: "What needs your attention.",
		exact: true,
	},
	{
		to: "/dashboard/$businessSlug/jobs",
		label: "Job listings",
		icon: BriefcaseIcon,
		description: "Write, publish and close openings.",
		exact: false,
	},
	{
		to: "/dashboard/$businessSlug/team",
		label: "Team",
		icon: UsersIcon,
		description: "Who else can post for this business.",
		exact: false,
		permission: "business.member.manage",
	},
	{
		to: "/dashboard/$businessSlug/settings",
		label: "Business details",
		icon: BuildingIcon,
		description: "Name, location and contact details.",
		exact: false,
		permission: "business.update",
	},
] as const satisfies readonly WorkspaceSection[];

/**
 * The workspace sections one actor may actually use, in order.
 *
 * The permission check lives here rather than in the layout so that the
 * declaration and the question it implies stay in the same file: a section that
 * names a permission cannot be rendered without one being asked.
 *
 * `"permission" in section` rather than `section.permission !== undefined`
 * because `as const` gives each entry exactly the keys it wrote, and the ones
 * with no requirement have no such key at all.
 */
export function workspaceSectionsFor(
	actor: Actor | null,
	businessId: BusinessId,
): readonly (typeof WORKSPACE_SECTIONS)[number][] {
	return WORKSPACE_SECTIONS.filter(
		(section) =>
			!("permission" in section) ||
			(actor !== null && can(actor, section.permission, { businessId })),
	);
}

/** Your own account, as opposed to any business you belong to. */
export const ACCOUNT_SECTIONS = [
	{
		to: "/settings/account",
		label: "Account",
		icon: UserIcon,
		description: "Your name, password and sessions.",
		exact: false,
	},
] as const satisfies readonly Section[];

/**
 * Where signing in lands you.
 *
 * Staff were sent to `/dashboard`, which asks which of *your* businesses you
 * want to work on — a question a moderator with no businesses cannot answer,
 * and a confusing first screen for one who happens to own something unrelated
 * to why they signed in. Anybody who can reach the console starts in it.
 *
 * Declared here rather than in the sign-in form so that every route into a
 * session — the password form, an OAuth callback, a future magic link — agrees.
 * `/signed-in` is the route that asks.
 */
export function landingFor(
	permissions: readonly Permission[],
): "/admin" | "/dashboard" {
	return permissions.includes("admin.access") ? "/admin" : "/dashboard";
}

/** The moderation console. Every screen behind `admin.access`. */
export const ADMIN_SECTIONS = [
	{
		to: "/admin",
		label: "Overview",
		icon: LayoutDashboardIcon,
		description: "What is waiting for a decision.",
		exact: true,
	},
	{
		to: "/admin/businesses",
		label: "Businesses",
		icon: BuildingIcon,
		description: "Review, approve and suspend employers.",
		exact: false,
	},
	{
		to: "/admin/jobs",
		label: "Listings",
		icon: BriefcaseIcon,
		description: "Every listing on the board.",
		exact: false,
	},
	{
		to: "/admin/users",
		label: "People",
		icon: UsersIcon,
		description: "Accounts and platform roles.",
		exact: false,
	},
	{
		to: "/admin/audit",
		label: "Audit log",
		icon: ScrollTextIcon,
		description: "Who did what, and when.",
		exact: false,
	},
] as const satisfies readonly Section[];
